import type { ReactNode } from 'react';
import { EvidencePanel } from '../../components/evidence';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { ErrorNotice, StatusBadge } from '../../components/ui/common';
import { referenceLabel, useApi, type ReferenceData } from '../../lib/api';
import { date, datetime, units } from '../../lib/format';
import { actionTypes, roundingLabels } from './action-labels';
import type { ActionRecord } from './action-record';
import { ActionEditButton } from './action-edit-button';
import './action-details.css';

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return <div><dt>{label}</dt><dd>{children}</dd></div>;
}

export function ActionDetailsDialog({ action, onClose, onReview, onEdit }: {
  action: ActionRecord; onClose: () => void; onReview: () => void; onEdit: () => void;
}) {
  const reference = useApi<ReferenceData>('/reference');
  const security = action.security ?? reference.data?.securities.find(item => item.id === action.securityId);
  const conversion = action.type === 'split' || action.type === 'consolidation';
  const confirmed = Object.entries(action.confirmedHoldings ?? {});
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }} title="Corporate action details" wide
    headerActions={<ActionEditButton status={action.status} onEdit={onEdit} />}>
    <div className="action-details">
      <div className="action-details-summary"><div><h3>{security ? referenceLabel(security) : action.securityId}</h3><p>{actionTypes[action.type].label}</p></div><StatusBadge status={action.status} /></div>
      <section aria-labelledby="action-details-event"><h3 id="action-details-event">Event details</h3>
        <dl className="action-details-grid">
          <Detail label={actionTypes[action.type].date}>{date(action.effectiveDate)}</Detail>
          {action.sequence != null && <Detail label="Order on this date">{action.sequence}</Detail>}
          {conversion && <>
            <Detail label="Share conversion">Every {units(action.oldUnits)} existing {Number(action.oldUnits) === 1 ? 'share becomes' : 'shares become'} {units(action.newUnits)} new {Number(action.newUnits) === 1 ? 'share' : 'shares'}.</Detail>
            <Detail label="Fractional shares">{action.rounding ? roundingLabels[action.rounding] : 'Not specified'}</Detail>
            {action.rounding && action.rounding !== 'none' && <Detail label="Share precision">{Number(action.unitPrecision ?? 0) === 0 ? 'Whole shares · 0 decimal places' : `${action.unitPrecision} decimal places`}</Detail>}
          </>}
          {action.type === 'return-of-capital' && <>
            <Detail label="Cash returned per original share">{units(action.cashPerOldUnit)} {security?.currency ?? ''}</Detail>
            <Detail label="Payment date">{date(action.paymentDate)}</Detail>
          </>}
          {action.type === 'rename' && <>
            <Detail label="New security name">{action.newName || 'Unchanged'}</Detail>
            <Detail label="New symbol">{action.newSymbol || 'Unchanged'}</Detail>
          </>}
        </dl>
      </section>
      <section aria-labelledby="action-details-supporting"><h3 id="action-details-supporting">Supporting information</h3>
        <dl className="action-details-grid"><Detail label="Legal effective date">{date(action.legalDate)}</Detail><Detail label="Record date">{date(action.recordDate)}</Detail></dl>
        <h4>Notes & announcement reference</h4><p className="action-details-notes">{action.notes || 'No notes or announcement reference recorded.'}</p>
      </section>
      {action.reconciliation?.confirmedTotal!==undefined && <section><h3>Confirmed post-event total</h3><p>{units(action.reconciliation.confirmedTotal)} shares</p></section>}
      {confirmed.length > 0 && <section aria-labelledby="action-details-holdings"><h3 id="action-details-holdings">Historical account quantities</h3>
        <dl className="action-details-grid">{confirmed.map(([id, quantity]) => <Detail key={id} label={reference.data?.registrations.find(item => item.id === id)?.name ?? id}>{units(quantity)} shares</Detail>)}</dl>
      </section>}
      <EvidencePanel entityType="corporate-action" entityId={action.id} showUpload={false} />
      <section aria-labelledby="action-details-history"><h3 id="action-details-history">Record history</h3>
        <dl className="action-details-grid">
          {action.createdAt && <Detail label="Created">{datetime(action.createdAt)}</Detail>}
          {action.updatedAt && <Detail label="Last updated">{datetime(action.updatedAt)}</Detail>}
          {action.appliedAt && <Detail label="Applied">{datetime(action.appliedAt)}</Detail>}
          {action.reversedAt && <Detail label="Reversed">{datetime(action.reversedAt)}</Detail>}
          {action.revision != null && <Detail label="Revision">{action.revision}</Detail>}
          <Detail label="Event ID">{action.id}</Detail>
        </dl>
        {action.reversalReason && <><h4>Reason for reversal</h4><p className="action-details-notes">{action.reversalReason}</p></>}
      </section>
      {reference.error && <ErrorNotice error={reference.error} retry={() => void reference.refetch()} />}
      <div className="form-actions"><Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={onReview}>Review impact</Button>
      </div>
    </div>
  </Dialog>;
}
